/**
 * API Protection — per-domain rules that sign an access token on matched API
 * requests (and optionally rate-limit them). Pick a domain, then review its rules
 * (ordered by priority) and toggle each one on/off, edit, or delete it.
 *
 * Backed by `/waf/api-protection/*` (Owl/Mlytics, customer-scoped). The rule
 * builder (create/edit) lives in `WafApiProtectionForm`.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, RotateCcw, Trash2, Pencil, Plus } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Badge,
  Skeleton,
  Button,
  IconButton,
  Switch,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Tooltip,
  TooltipProvider,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafApiProtectionRules,
  useSetApiProtectionRuleEnabled,
  useDeleteApiProtectionRule,
  fmtEpoch,
  wafExtractError,
  type APIProtectionRule,
  type APIProtectionAction,
  type ATPCondition,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import styles from './WafApiProtection.module.css';

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;
const mono = { fontFamily: 'var(--vn-font-mono)' } as const;

/** Build the create/edit form URL (+ optional rule id). */
function formHref(serviceId: string, domain: string, id?: string) {
  const q = new URLSearchParams({ domain });
  if (id) q.set('id', id);
  return `/services/waf/${serviceId}/api-protection/${id ? 'edit' : 'new'}?${q.toString()}`;
}

/** Read-only detail (rule settings + implement script). */
function detailHref(serviceId: string, domain: string, id: string) {
  return `/services/waf/${serviceId}/api-protection/detail?domain=${encodeURIComponent(domain)}&id=${encodeURIComponent(id)}`;
}

/** A short, human summary of the action (token param + rate-limit). */
function actionDetail(a: APIProtectionAction): string {
  const parts: string[] = [];
  if (a.param) parts.push(a.param);
  if (a.rate_limit_enable) parts.push('rate-limited');
  return parts.join(' · ');
}

/** Compact one-line rendering of a condition. */
function condText(c: ATPCondition): string {
  const field = c.key ? `${c.field}[${c.key}]` : c.field;
  return [field, c.operator, c.value ?? ''].filter(Boolean).join(' ');
}

/** First couple conditions as chips + a `+N` overflow. */
function conditionsCell(conds: ATPCondition[] | undefined, max = 2) {
  const items = conds ?? [];
  if (items.length === 0) return muted('—');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...mono, fontSize: 'var(--vn-font-size-sm)' }}>
      {items.slice(0, max).map(condText).join('  •  ')}
      {items.length > max ? muted(` +${items.length - max}`) : null}
    </span>
  );
}

/** Inline enable/disable toggle for a rule. */
function EnableCell({ serviceId, domain, rule }: { serviceId: string; domain: string; rule: APIProtectionRule }) {
  const { show } = useToast();
  const setEnabled = useSetApiProtectionRuleEnabled(serviceId, {
    onSuccess: (_d, vars) => { show({ tone: 'success', title: vars.enable ? 'Rule enabled' : 'Rule disabled' }); trackFeatureAction('waf', 'api_rule', vars.enable ? 'enable' : 'disable'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <Switch
        checked={rule.enable}
        disabled={setEnabled.isPending}
        aria-label={`Enable ${rule.name}`}
        onCheckedChange={(enable) => setEnabled.mutate({ domain, id: rule.id, enable })}
      />
    </div>
  );
}

/** Edit + delete actions for a rule. */
function ActionsCell({ serviceId, domain, rule }: { serviceId: string; domain: string; rule: APIProtectionRule }) {
  const { show } = useToast();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteApiProtectionRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule deleted' }); trackFeatureAction('waf', 'api_rule', 'delete'); setConfirmDelete(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        <Tooltip content="Edit" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Edit rule" onClick={() => navigate(formHref(serviceId, domain, rule.id))}>
            <Pencil size={14} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Delete" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Delete rule" onClick={() => setConfirmDelete(true)}>
            <Trash2 size={14} />
          </IconButton>
        </Tooltip>
      </TooltipProvider>
      {confirmDelete && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}
          title="Delete rule?"
          description={`Delete the rule "${rule.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ domain, id: rule.id })}
        />
      )}
    </div>
  );
}

/** API-protection rules table for a domain. */
function RulesCard({ serviceId, domain }: { serviceId: string; domain: string }) {
  const navigate = useNavigate();
  const goAdd = () => navigate(formHref(serviceId, domain));
  const { data, isLoading, isError, refetch } = useWafApiProtectionRules(serviceId, domain);
  const rules = data ?? [];

  const columns = useMemo<ColumnDef<APIProtectionRule, unknown>[]>(
    () => [
      {
        id: 'priority',
        header: '#',
        enableSorting: false,
        cell: ({ row }) => muted(row.original.priority ?? '—'),
      },
      {
        id: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name}</span>,
      },
      {
        id: 'conditions',
        header: 'Conditions',
        enableSorting: false,
        cell: ({ row }) => conditionsCell(row.original.conditions),
      },
      {
        id: 'action',
        header: 'Action',
        enableSorting: false,
        cell: ({ row }) => {
          const detail = actionDetail(row.original.action);
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Badge tone="neutral" appearance="subtle" size="sm">API Protection</Badge>
              {detail ? <span style={{ ...mono, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{detail}</span> : null}
            </span>
          );
        },
      },
      {
        id: 'enable',
        header: 'Enabled',
        enableSorting: false,
        cell: ({ row }) => <EnableCell serviceId={serviceId} domain={domain} rule={row.original} />,
      },
      {
        id: 'updated',
        header: 'Updated',
        enableSorting: false,
        cell: ({ row }) => muted(fmtEpoch(row.original.updated_at)),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => <ActionsCell serviceId={serviceId} domain={domain} rule={row.original} />,
      },
    ],
    [serviceId, domain],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>API protection rules{rules.length ? ` (${rules.length})` : ''}</CardTitle>
      </CardHeader>
      <CardBody>
        <div className={styles.toolbar}>
          <div className={styles.spacer} />
          <div className={styles.actions}>
            <Button variant="secondary" startIcon={<RotateCcw size={15} />} onClick={() => refetch()} aria-label="Refresh rules">
              Refresh
            </Button>
            <Button startIcon={<Plus size={15} />} onClick={goAdd}>Add rule</Button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} style={{ height: 44 }} />)}
          </div>
        ) : isError ? (
          <Alert tone="danger" title="Couldn't load API protection rules" actions={<button onClick={() => refetch()}>Retry</button>}>
            The WAF service couldn't return this domain's API protection rules. Try again.
          </Alert>
        ) : rules.length === 0 ? (
          <EmptyState
            icon={<KeyRound size={20} />}
            title="No API protection rules yet"
            description="This domain has no API protection rules. Create one to require a signed token (and optionally rate-limit) matched API requests."
            actions={<Button startIcon={<Plus size={15} />} onClick={goAdd}>Add rule</Button>}
          />
        ) : (
          <DataTable
            columns={columns}
            data={rules}
            getRowId={(r) => r.id}
            onRowClick={(r) => navigate(detailHref(serviceId, domain, r.id))}
            emptyState="No API protection rules."
          />
        )}
      </CardBody>
    </Card>
  );
}

export function WafApiProtection({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const domain = scope.subdomain;
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<KeyRound size={22} />}
        title="API Protection"
        description="Require a signed access token (and optionally rate-limit) for matched API requests, per subdomain."
      />

      <div className={styles.scopeBar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<KeyRound size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to manage API protection rules for. Proxy a record first."
        />
      ) : !domain ? (
        <EmptyState
          icon={<KeyRound size={20} />}
          title="Select a subdomain"
          description="Pick a domain and subdomain above to manage its API protection rules."
        />
      ) : (
        <RulesCard serviceId={serviceId} domain={domain} />
      )}
    </Container>
  );
}

export default WafApiProtection;
